import { useMemo } from "react";
import { Bell } from "lucide-react";

/**
 * ReminderEditor — a compact chip-selector for reminder offsets, used inside
 * the event editor modal. Emits `onChange(newReminders)` where each item is
 * `{offset_minutes, channels}`. Custom offsets are entered as a positive
 * integer of minutes.
 */

export const REMINDER_PRESETS = [
    { key: "at",    label: "At event time",   offset_minutes: 0 },
    { key: "5m",    label: "5 min before",    offset_minutes: 5 },
    { key: "10m",   label: "10 min before",   offset_minutes: 10 },
    { key: "15m",   label: "15 min before",   offset_minutes: 15 },
    { key: "30m",   label: "30 min before",   offset_minutes: 30 },
    { key: "1h",    label: "1 hour before",   offset_minutes: 60 },
    { key: "2h",    label: "2 hours before",  offset_minutes: 120 },
    { key: "1d",    label: "1 day before",    offset_minutes: 60 * 24 },
    { key: "2d",    label: "2 days before",   offset_minutes: 60 * 48 },
    { key: "1w",    label: "1 week before",   offset_minutes: 60 * 24 * 7 },
];

export default function ReminderEditor({ value = [], onChange }) {
    const selectedOffsets = useMemo(() => new Set(value.map(r => r.offset_minutes)), [value]);

    const toggle = (offset) => {
        if (selectedOffsets.has(offset)) {
            onChange(value.filter(r => r.offset_minutes !== offset));
        } else {
            onChange([...value, { offset_minutes: offset, channels: ["in_app"] }]);
        }
    };

    const toggleChannel = (offset, channel) => {
        onChange(value.map(r => {
            if (r.offset_minutes !== offset) return r;
            const has = (r.channels || []).includes(channel);
            const next = has ? (r.channels || []).filter(c => c !== channel) : [...(r.channels || []), channel];
            return { ...r, channels: next.length ? next : ["in_app"] };
        }));
    };

    const addCustom = () => {
        const raw = window.prompt("Reminder: how many minutes before the event?", "45");
        const mins = parseInt(raw, 10);
        if (!isFinite(mins) || mins < 0 || mins > 525600) return;
        if (selectedOffsets.has(mins)) return;
        onChange([...value, { offset_minutes: mins, channels: ["in_app"] }]);
    };

    return (
        <div className="space-y-2" data-testid="reminder-editor">
            <div className="flex items-center gap-1.5 text-xs uppercase tracking-wider text-[var(--text-ink)]/55">
                <Bell size={12}/> Reminders
            </div>
            <div className="flex flex-wrap gap-1.5">
                {REMINDER_PRESETS.map(p => {
                    const on = selectedOffsets.has(p.offset_minutes);
                    return (
                        <button key={p.key} type="button"
                                onClick={() => toggle(p.offset_minutes)}
                                data-testid={`reminder-preset-${p.key}`}
                                className={`text-[11px] px-2.5 py-1 rounded-full border ${on ? "brand-gradient text-white border-transparent" : "btn-ghost border-black/10"}`}>
                            {p.label}
                        </button>
                    );
                })}
                <button type="button" onClick={addCustom}
                        data-testid="reminder-preset-custom"
                        className="text-[11px] px-2.5 py-1 rounded-full border-2 border-dashed border-black/15 btn-ghost">
                    + Custom
                </button>
            </div>
            {value.length > 0 && (
                <div className="text-[11px] text-[var(--text-ink)]/55 space-y-1 pt-1">
                    {value.slice().sort((a, b) => a.offset_minutes - b.offset_minutes).map(r => (
                        <div key={r.offset_minutes} className="flex items-center gap-2" data-testid={`reminder-row-${r.offset_minutes}`}>
                            <span className="w-32">{labelFor(r.offset_minutes)}</span>
                            <label className="inline-flex items-center gap-1 cursor-pointer">
                                <input type="checkbox" checked={(r.channels || []).includes("in_app")}
                                       onChange={() => toggleChannel(r.offset_minutes, "in_app")}
                                       data-testid={`reminder-ch-in_app-${r.offset_minutes}`}/>
                                In-app
                            </label>
                            <label className="inline-flex items-center gap-1 cursor-pointer">
                                <input type="checkbox" checked={(r.channels || []).includes("email")}
                                       onChange={() => toggleChannel(r.offset_minutes, "email")}
                                       data-testid={`reminder-ch-email-${r.offset_minutes}`}/>
                                Email
                            </label>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

function labelFor(mins) {
    if (mins === 0) return "At event time";
    if (mins < 60) return `${mins} min before`;
    if (mins < 60 * 24) return `${(mins / 60).toFixed(mins % 60 ? 1 : 0)}h before`;
    if (mins < 60 * 24 * 7) return `${(mins / 60 / 24).toFixed(mins % (60 * 24) ? 1 : 0)}d before`;
    return `${(mins / 60 / 24 / 7).toFixed(1)}w before`;
}
